import React, { useState, useEffect } from 'react';
import { Settings, ChevronUp, ChevronDown, X, Check, Shield } from 'lucide-react';
import { useI18n } from '../../i18n';

const CookieBanner: React.FC = () => {
  const { t } = useI18n();
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // 必需的Cookie，始终启用
    analytics: false,
    marketing: false,
    functional: false,
  });

  useEffect(() => {
    // 检查用户是否已经做出过选择
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // 延迟显示横幅，提供更好的用户体验
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = {
      essential: true,
      analytics: true,
      marketing: true,
      functional: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setIsVisible(false);
  };

  const handleEssentialOnly = () => {
    const consent = {
      essential: true,
      analytics: false,
      marketing: false,
      functional: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    const consent = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setIsVisible(false);
  };

  const handleClose = () => {
    // 临时关闭，但不保存选择，下次刷新还会显示
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up pb-safe">
      {/* 背景模糊层 */}
      <div className="absolute inset-0 backdrop-blur-2xl bg-gradient-to-t from-white/95 via-white/90 to-white/80 border-t border-gray-200/50 shadow-2xl"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* 主要内容区 */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          {/* 左侧：图标和描述 */}
          <div className="flex items-start gap-4 flex-1">
            {/* Unilumin品牌图标 */}
            <div className="flex-shrink-0">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/25 transform transition-transform hover:scale-105">
                <img 
                  src="/logo_24.svg" 
                  alt="Unilumin" 
                  className="w-10 h-10 brightness-0 invert"
                />
              </div>
            </div>
            
            <div className="flex-1 pt-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-semibold text-gray-900 tracking-tight">
                  {t('cookie.title')}
                </h3>
                <Shield className="w-4 h-4 text-blue-600" />
              </div>
              <div className="space-y-2">
                <p className="text-sm text-gray-700 leading-relaxed">
                  {t('cookie.description')}
                </p>
                <p className="text-xs text-blue-600 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  <span>{t('cookie.gdprCompliant')}</span>
                </p>
              </div>
            </div>
          </div>

          {/* 右侧：操作按钮 */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 lg:flex-shrink-0">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="group flex items-center justify-center px-5 py-2.5 text-sm font-medium bg-white/80 text-gray-700 rounded-xl border border-gray-300/50 hover:bg-white hover:border-gray-400/50 transition-all duration-200 backdrop-blur-sm shadow-sm hover:shadow"
            >
              <Settings className="w-4 h-4 mr-2 group-hover:rotate-90 transition-transform duration-300" />
              {t('cookie.customizeSettings')}
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 ml-1" />
              ) : (
                <ChevronDown className="w-4 h-4 ml-1" />
              )}
            </button>
            <button
              onClick={handleEssentialOnly}
              className="px-5 py-2.5 text-sm font-medium bg-gray-600/90 text-white rounded-xl hover:bg-gray-700 transition-all duration-200 shadow-md hover:shadow-lg backdrop-blur-sm"
            >
              {t('cookie.essentialOnly')}
            </button>
            <button
              onClick={handleAcceptAll}
              className="px-6 py-2.5 text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30"
            >
              {t('cookie.acceptAll')}
            </button>
          </div>
        </div>

        {/* 展开的详细设置 */}
        {isExpanded && (
          <div className="mt-6 pt-6 border-t border-gray-200/50 animate-fade-in">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* 必需Cookie */}
              <div className="group flex items-start gap-3 p-4 rounded-xl bg-white/60 backdrop-blur-sm border border-gray-200/50 transition-all hover:bg-white/80 hover:border-gray-300/50">
                <input
                  type="checkbox"
                  checked={preferences.essential}
                  disabled
                  className="mt-1 rounded-md border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 disabled:opacity-50 w-4 h-4"
                />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 text-sm mb-1">{t('cookie.essentialCookies')}</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {t('cookie.essentialCookiesDesc')}
                  </p>
                </div>
              </div>

              {/* 分析Cookie */}
              <div className="group flex items-start gap-3 p-4 rounded-xl bg-white/60 backdrop-blur-sm border border-gray-200/50 transition-all hover:bg-white/80 hover:border-gray-300/50">
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={(e) =>
                    setPreferences({ ...preferences, analytics: e.target.checked })
                  }
                  className="mt-1 rounded-md border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 w-4 h-4 cursor-pointer"
                />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 text-sm mb-1">{t('cookie.analyticsCookies')}</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {t('cookie.analyticsCookiesDesc')}
                  </p>
                </div>
              </div>

              {/* 功能Cookie */}
              <div className="group flex items-start gap-3 p-4 rounded-xl bg-white/60 backdrop-blur-sm border border-gray-200/50 transition-all hover:bg-white/80 hover:border-gray-300/50">
                <input
                  type="checkbox"
                  checked={preferences.functional}
                  onChange={(e) =>
                    setPreferences({ ...preferences, functional: e.target.checked })
                  }
                  className="mt-1 rounded-md border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 w-4 h-4 cursor-pointer"
                />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 text-sm mb-1">{t('cookie.functionalCookies')}</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {t('cookie.functionalCookiesDesc')}
                  </p>
                </div>
              </div>

              {/* 营销Cookie */}
              <div className="group flex items-start gap-3 p-4 rounded-xl bg-white/60 backdrop-blur-sm border border-gray-200/50 transition-all hover:bg-white/80 hover:border-gray-300/50">
                <input
                  type="checkbox"
                  checked={preferences.marketing}
                  onChange={(e) =>
                    setPreferences({ ...preferences, marketing: e.target.checked })
                  }
                  className="mt-1 rounded-md border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 w-4 h-4 cursor-pointer"
                />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 text-sm mb-1">{t('cookie.marketingCookies')}</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {t('cookie.marketingCookiesDesc')}
                  </p>
                </div>
              </div>
            </div>

            {/* 保存自定义设置按钮 */}
            <div className="mt-5 flex justify-end">
              <button
                onClick={handleSavePreferences}
                className="px-6 py-2.5 text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg shadow-blue-500/25"
              >
                {t('cookie.savePreferences')}
              </button>
            </div>
          </div>
        )}

        {/* 关闭按钮 */}
        {/* <button
          onClick={handleClose}
          className="absolute top-4 -right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100/50 rounded-lg transition-all duration-200"
          aria-label="关闭"
        >
          <X className="w-5 h-5" />
        </button> */}
      </div>
    </div>
  );
};

export default CookieBanner;